import { useTranslation } from 'react-i18next';

import { ArrowUp } from 'lucide-react';

export const PrivacyTermsPage = ({ isDialog = false }) => {
    const { t } = useTranslation();

    const goToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    };

    return (
        <div className="w-full bg-popover p-6 pb-10 pt-0 text-text overflow-auto">
            {!isDialog && <div className="text-[32px] font-semibold text-center mb-10">Privacy & Terms</div>}
            <div>
                <div className="text-[20px] font-semibold mt-5">1. Account and Eligibility</div>
                <ul className="ml-6">
                    <li className="list-outside list-disc">
                        Users must provide accurate information during registration.
                    </li>
                    <li className="list-outside list-disc">
                        Users are responsible for maintaining the security of their accounts.
                    </li>
                    <li className="list-outside list-disc">
                        Eureka Codes is not liable for unauthorized account access.
                    </li>
                </ul>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">2. Subscription and Payment</div>
                <ul className="ml-6">
                    <li className="list-outside list-disc">Eureka Codes operates on a subscription-based model.</li>
                    <li className="list-outside list-disc">
                        Payments are processed securely via third-party providers.
                    </li>
                    <li className="list-outside list-disc">Refunds follow our Refund Policy (see below).</li>
                </ul>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">3. Acceptable Use Policy (AUP)</div>
                Users must not:
                <ul className="ml-6">
                    <li className="list-outside list-disc">Use the service for illegal or harmful activities.</li>
                    <li className="list-outside list-disc">
                        Attempt to hack, disrupt, or exploit system vulnerabilities.
                    </li>
                    <li className="list-outside list-disc">
                        Share or distribute unauthorized copies of Eureka Codes content.
                    </li>
                    <li className="list-outside list-disc">
                        Engage in excessive API usage that negatively impacts service availability.
                    </li>
                    <li className="list-outside list-disc font-medium">
                        Engage in spam activities or automated data scraping.
                    </li>
                    <li className="list-outside list-disc font-medium">
                        Use the platform to train AI models without explicit permission.
                    </li>
                </ul>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">4. Intellectual Property</div>
                <ul className="ml-6">
                    <li className="list-outside list-disc">
                        All content and software related to Eureka Codes are our exclusive property.
                    </li>
                    <li className="list-outside list-disc">
                        Users retain rights to their own uploaded content but grant Eureka Codes permission to use it
                        for platform operations.
                    </li>
                </ul>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">5. Service Availability and Termination</div>
                <ul className="ml-6">
                    <li className="list-outside list-disc">
                        We reserve the right to modify, suspend, or terminate the Service at any time.
                    </li>
                    <li className="list-outside list-disc">
                        Users can cancel their subscription at any time, subject to our Refund Policy.
                    </li>
                </ul>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">6. Limitation of Liability</div>
                <ul className="ml-6">
                    <li className="list-outside list-disc">
                        Eureka Codes is provided "as is" without warranties of any kind.
                    </li>
                    <li className="list-outside list-disc">
                        We are not responsible for any data loss, service downtime, or indirect damages.
                    </li>
                </ul>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">7. Refund Policy</div>
                Eureka Codes offers refunds under the following conditions:
                <div className="ml-3">
                    <div className="font-semibold">1. Free Trial Policy</div>
                    <ul className="ml-6 mb-1">
                        <li className="list-outside list-disc">
                            Eureka Codes provides a <span className="font-medium">free trial</span> for new users.
                        </li>
                        <li className="list-outside list-disc">
                            Refunds are <span className="font-medium">not available</span> during or after the free
                            trial period.
                        </li>
                    </ul>
                    <div className="font-semibold">2. Monthly Subscriptions</div>
                    <ul className="ml-6 mb-1">
                        <li className="list-outside list-disc">
                            Refunds are <span className="font-medium">not available</span> for monthly subscriptions
                            once a payment has been processed.
                        </li>
                        <li className="list-outside list-disc">
                            Users may cancel their subscription at any time, but the cancellation will take effect at
                            the end of the billing period.
                        </li>
                    </ul>
                    <div className="font-semibold">3. Annual Subscriptions</div>
                    <ul className="ml-6 mb-1">
                        <li className="list-outside list-disc">
                            Users may request a refund within <span className="font-medium">14 days of purchase</span>
                            if the service has not been extensively used.
                        </li>
                        <li className="list-outside list-disc">
                            Refunds will be issued on a prorated basis based on usage and time elapsed.
                        </li>
                    </ul>
                    <div className="font-semibold">4. Exceptional Cases</div>
                    <ul className="ml-6 mb-1">
                        <li className="list-outside list-disc">
                            Refunds may be granted under exceptional circumstances, such as accidental duplicate
                            payments or technical failures that prevent service usage.
                        </li>
                        <li className="list-outside list-disc">
                            Users must submit refund requests to lemon@lemoncloud.io with details of the issue.
                        </li>
                    </ul>
                    <div className="font-semibold">5. Chargebacks and Disputes</div>
                    <ul className="ml-6">
                        <li className="list-outside list-disc">
                            Unauthorized chargebacks may result in account suspension.
                        </li>
                        <li className="list-outside list-disc">
                            Users are encouraged to contact support before initiating a chargeback.
                        </li>
                    </ul>
                </div>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">8. Governing Law and Dispute Resolution</div>
                <ul className="ml-6">
                    <li className="list-outside list-disc">
                        These Terms are governed by the laws of the State of Delaware, USA.
                    </li>
                    <li className="list-outside list-disc">
                        Disputes will be resolved through arbitration in accordance with applicable laws.
                    </li>
                </ul>
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">9. Changes to Terms</div>
                We may update these Terms from time to time. Continued use of Eureka Codes after changes indicates
                acceptance of the new Terms.
            </div>
            <div>
                <div className="text-[20px] font-semibold mt-5">10. Contact Information</div>
                For any questions about these Terms, contact us at lemon@lemoncloud.io.
            </div>
            {!isDialog && (
                <button
                    className="!fixed bottom-[52px] right-[52px] max-sm:bottom-6 max-sm:right-[18px] p-3 rounded-full bg-background hover:bg-accent transition-colors"
                    onClick={() => goToTop()}
                >
                    <ArrowUp className="w-5 h-5" />
                </button>
            )}
        </div>
    );
};
